import { useState } from 'react';
import { useProgramSearch } from '../hooks/useProgramSearch.js';
import { useFreshness } from '../hooks/useFreshness.js';
import { SearchBar } from '../components/SearchBar.jsx';
import { Notices } from '../components/Notices.jsx';
import { FilterChips, chipsFor } from '../components/FilterChips.jsx';
import { ResultsTable } from '../components/ResultsTable.jsx';
import { ProgramCardGrid } from '../components/ProgramCardGrid.jsx';
import { ViewToggle } from '../components/ViewToggle.jsx';
import { DetailDrawer } from '../components/DetailDrawer.jsx';
import { InitialLoader, ResultsOverlay } from '../components/SearchLoader.jsx';

/**
 * Search Programs — unchanged behaviour, lifted out of App.jsx so a second
 * search page (Universities) can sit beside it without the two sharing a
 * component that has to know about both.
 */
export default function ProgramsPage() {
  const {
    filters, sort, options, data, hits, error, loading, stale,
    dispatch, refreshFromIndex, cleanupNote,
  } = useProgramSearch();

  const freshnessNote = useFreshness('programs', refreshFromIndex);

  // Which result is open in the detail drawer. Just an id — the drawer fetches
  // the full MongoDB record itself. Search is untouched by this.
  const [openId, setOpenId] = useState(null);

  // List View or Card View. Default is 'list' as specified in product requirements.
  const [view, setView] = useState('list');

  const facets = data?.facets;

  // No count in the headline — "Search across 72,611 Programs" read as a
  // number the reader had to reconcile with the actual result count just
  // below it, especially once a filter moved one but not the other. The live,
  // query-driven count still has exactly one place it belongs: the result bar
  // right above the table (`.resbar`, from `data.found`), where a change in it
  // is a change in what is actually showing.
  const title = 'Search in Programs';

  // The result count itself is withheld until a filter narrows the search.
  // The unfiltered catalogue total is the same number every time regardless of
  // anything the user did — showing it up front reads as a headline stat, not
  // an answer to a question they asked. `chipsFor` is the same "is anything
  // actually filtered?" check the chip row already makes, so this can never
  // disagree with what the chip row shows.
  const hasFilters = chipsFor(filters, options, dispatch).length > 0;

  return (
    <>
      {error && <div id="error"><div className="err">{error}</div></div>}

      <Notices
        data={data}
        filters={filters}
        options={options}
        dispatch={dispatch}
        onSearchExact={() => dispatch({ type: 'SET_QUERY', value: `"${filters.q.trim()}"` })}
      />

      {/* One filter card with exactly 3 rows — all of it is SearchBar now; the
          Refine rail is gone, its fields folded into Row 3:
          Row 1: Search in Programs | Keyword
          Row 2: University Country | Student Nationality | Course Type
          Row 3: University Name | Duration | English Proficiency | Quick Filters
                 | Course Intake (Exact) | Active Status | Sort By */}
      <div className="filterbar">
        <SearchBar
          title={title}
          filters={filters}
          sort={sort}
          options={options}
          facets={facets}
          loading={loading}
          dispatch={dispatch}
        />
      </div>

      <FilterChips filters={filters} options={options} dispatch={dispatch} />

      <div className="cols">
        <div className="results">
          <div className="resbar">
            <div className="resbar-left">
              <div className="n" id="count">
                {!data || stale ? '…'
                  : hasFilters ? <><b>{data.found.toLocaleString()}</b> programs found</>
                    : 'Showing all programs'}
              </div>
            </div>

            <div className="resbar-right">
              <ViewToggle view={view} onChange={setView} />
            </div>
          </div>

          {/* `data` stays null only until the first response for THIS search
              lands — the initial-load branded loader covers exactly that gap.
              Once a response has landed once, `data` is never cleared again,
              so a later filter/search/sort/page change keeps the previous
              rows on screen (dimmed via `is-loading`) with the overlay on top,
              rather than the page ever looking empty mid-request. */}
          <div className={`results-area${data && loading ? ' is-loading' : ''}`}>
            <div id="tableWrap" aria-busy={data && loading ? 'true' : 'false'}>
              {!data
                ? (!error && <InitialLoader label="Loading programs…" view={view} />)
                : (
                  view === 'card' ? (
                    <ProgramCardGrid
                      hits={hits}
                      found={data.found}
                      onOpen={setOpenId}
                      universityAssets={options?.universityAssets}
                    />
                  ) : (
                    <ResultsTable hits={hits} found={data.found} onOpen={setOpenId} />
                  )
                )}
            </div>
            <ResultsOverlay active={Boolean(data) && loading} />
          </div>

          <div className="more" id="more">
            {data && hits.length < data.found && (
              <button
                type="button"
                id="loadMore"
                disabled={loading}
                aria-busy={loading}
                onClick={() => dispatch({ type: 'LOAD_MORE' })}
              >
                {loading
                  ? 'Loading…'
                  : `Load more (${hits.length.toLocaleString()} of ${data.found.toLocaleString()})`}
              </button>
            )}
          </div>
        </div>
      </div>

      {freshnessNote && (
        <div id="freshnessNote" className="freshnote show">{freshnessNote}</div>
      )}

      {/* Non-blocking, self-clearing — never a modal, never a reason the
          parent change itself waited. See useDismissingNote / filterDependency.js. */}
      {cleanupNote && (
        <div id="filterCleanupNote" className="freshnote show" role="status">{cleanupNote}</div>
      )}

      <DetailDrawer kind="programs" id={openId} onClose={() => setOpenId(null)} />
    </>
  );
}
